import {CommonModule} from '@angular/common';
import {Component, Inject, Input} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule} from '@angular/forms';
import {TranslationService} from '@emma-labs/shared/angular';
import {
  DerivedUnderwritingProfileStatus,
  Dropdown,
  ENVIRONMENT,
  EnvironmentBroker,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BadgeModule} from 'primeng/badge';
import {CardModule} from 'primeng/card';
import {DividerModule} from 'primeng/divider';
import {DropdownModule} from 'primeng/dropdown';
import {BaseSectionComponent} from '../../base-section/base-section.component';

type InfoCardProps = {
  policyNumber: string;
  status: {value: DerivedUnderwritingProfileStatus; text: string};
  underwriters: {fullName: string; agentId: string}[];
  underwriterId?: string;
};

@UntilDestroy()
@Component({
  selector: 'em-underwriting-info-card',
  imports: [CommonModule, BadgeModule, CardModule, DropdownModule, ReactiveFormsModule, DividerModule],
  templateUrl: './underwriting-info-card.component.html',
})
export class UnderwritingInfoCardComponent extends BaseSectionComponent<
  FormControl<string | null>,
  InfoCardProps,
  string | undefined
> {
  @Input({required: true})
  override set props(info: InfoCardProps) {
    this.info = info;
    this.underwriterOptions = this.getUnderwriterOptions(info.underwriters);

    if (info.underwriterId) {
      this.form.patchValue(info.underwriterId, {emitEvent: false});
    }
  }

  info!: {
    policyNumber: string;
    status: {value: DerivedUnderwritingProfileStatus; text: string};
  };
  underwriterOptions?: Dropdown<string | null>[];
  protected readonly status = DerivedUnderwritingProfileStatus;

  constructor(
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
    private readonly fb: FormBuilder,
    private readonly translate: TranslationService,
  ) {
    super(environment);

    this.form = this.createForm();
  }

  private createForm(): FormControl<string | null> {
    const form = this.fb.control(null);

    form.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.propsChange.emit({output: value ?? undefined}));

    return form;
  }

  private getUnderwriterOptions(
    underwriters: {fullName: string; agentId: string}[],
  ): Dropdown<string | null>[] {
    return [
      {
        name: this.translate.currentLanguageIsFr() ? 'Aucun' : 'None',
        value: null,
      },
      ...underwriters.map((underwriter) => ({
        name: underwriter.fullName,
        value: underwriter.agentId,
      })),
    ];
  }
}
